// Orquestra a criação/atualização do Requerimento de um pedido do wizard de
// Requerimentos junto à API de Protocolo — reaproveita o protocoloService já
// usado (e funcionando) pelo Protocolo Online, sem alterá-lo.
import { protocoloService } from '../../../../services';
import { resolveProtocoloDados } from './protocoloResolverService';
import { salvarDadosRequerimento } from './dadosRequerimentoService';
import { criarPessoaJuridicaSociedade } from './sociedadePessoaJuridicaService';
import logger from '../../../../utils/logger';

// Departamento destino padrão do módulo — pode ser sobrescrito por pedido via
// `protocoloConfig.departamentoDestino` (ex: Comissões usa '67', não '88').
const DEPARTAMENTO_DESTINO_PADRAO = '88';
const SITUACAO_PENDENTE = 4;
const SITUACAO_AGUARDANDO_PAGAMENTO = 3;
const SITUACAO_CANCELADO = 9;

/**
 * Cria o rascunho do Requerimento (situacao 4 = Pendente), assim que o
 * formulário do pedido é concluído. Se a pessoa sair antes de concluir, o
 * registro fica nessa situação até ela retomar o pedido (rascunho salvo no
 * navegador, ver rascunhoPedidoService) ou descartá-lo (cancelarRequerimento).
 */
export async function criarRascunhoRequerimento({ config, formData, user, oabData }) {
  const { modeloRequerimento, tiposAssunto, tipoPedido, comissaoRequerida } = resolveProtocoloDados(config.protocoloConfig, formData, oabData);
  const { pessoaId, usuarioId } = protocoloService.extrairIdsUsuario(user);

  // Registro de Sociedade (194/322): o /criar exige em nomeSociedade o ID da
  // Pessoa da sociedade, que ainda não existe — cria (ou reaproveita) a PJ antes.
  const nomeSociedade = config.protocoloConfig.criarSociedadePessoaJuridica
    ? await criarPessoaJuridicaSociedade(formData)
    : undefined;

  const response = await protocoloService.criarProtocolo({
    pessoaId: Number(pessoaId),
    usuarioId: Number(usuarioId),
    situacao: SITUACAO_PENDENTE,
    modeloRequerimento,
    tiposAssunto,
    // tipoPedido só é exigido pela API quando situacao=1 (não é o caso aqui, é
    // sempre 4/Pendente) — mandamos assim mesmo, sem custo, pra já ficar
    // populado se a criação em si um dia passar a finalizar direto.
    tipoPedido,
    comissaoRequerida,
    nomeSociedade,
    registroConselho: oabData?.registroConselho,
    // resolvido no login a partir de oab.tipoInscricaoAtual (ver authService.js);
    // undefined quando a pessoa não é inscrita, e aí o axios omite a chave.
    tipoInscricaoRemetente: oabData?.tipoInscricaoRemetente
  });

  const result = response.data || response;
  const requerimentoId = result?.requerimentoId;

  // Best-effort: grava os campos do formulário em DadosRequerimento, se o
  // pedido declarar `dadosRequerimentoConfig` (ver dadosRequerimentoService.js).
  // Uma falha aqui não pode travar o andamento do pedido — o requerimento já
  // foi criado, e essa gravação é só um enriquecimento de dados.
  if (requerimentoId && config.dadosRequerimentoConfig) {
    try {
      await salvarDadosRequerimento({
        requerimentoId,
        usuarioId,
        modeloRequerimentoId: modeloRequerimento,
        formData,
        dadosRequerimentoConfig: config.dadosRequerimentoConfig,
        steps: config.steps
      });
    } catch (err) {
      logger.warn('Não foi possível salvar os dados do requerimento em DadosRequerimento:', err.message);
    }
  }

  // Dois números da mesma SolicitacaoServico, com papéis diferentes:
  // - solicitacaoId (SolicitacaoServico.ID): uso interno — é o que /finalizar/:id e
  //   /status/:id esperam. Nunca é mostrado pra pessoa.
  // - numeroPedido (SolicitacaoServico.Protocolo, `protocoloNumero` no /criar): o
  //   "Nº do Pedido" mostrado pra pessoa (tela de sucesso, Resumo em PDF, retomada) —
  //   o mesmo que GET /listar devolve em Minhas Solicitações e que Pendências exibe.
  // O NumeroRequerimento da API não é exibido em lugar nenhum.
  return { solicitacaoId: result?.solicitacaoId, numeroPedido: result?.protocoloNumero ?? null, requerimentoId };
}

/**
 * Pedidos COM taxa: nunca gera Protocolo/DocumentoProtocolo final — só marca o
 * Requerimento como Aguardando Pagamento (situacao 3).
 */
export async function marcarAguardandoPagamento(requerimentoId) {
  return protocoloService.atualizarStatusRequerimento(requerimentoId, SITUACAO_AGUARDANDO_PAGAMENTO);
}

/**
 * Descarta um rascunho que a pessoa não vai concluir (situacao 9 = Cancelado),
 * pra não ficar Pendente pra sempre em Minhas Solicitações.
 */
export async function cancelarRequerimento(requerimentoId) {
  return protocoloService.atualizarStatusRequerimento(requerimentoId, SITUACAO_CANCELADO);
}

/**
 * Só dá pra retomar/descartar o que ainda é rascunho de verdade: Pendente (4) e
 * sem DocumentoProtocolo. Recebe a situação como vem da API (string ou número).
 */
export const isRascunhoAberto = (situacao, documentoProtocoloId) =>
  String(situacao) === String(SITUACAO_PENDENTE) && !documentoProtocoloId;

/**
 * Pedido com taxa já finalizado pela pessoa, esperando o pagamento (3) — é
 * quando Minhas Solicitações mostra de novo o PIX/boleto.
 */
export const isAguardandoPagamento = (situacao) =>
  String(situacao) === String(SITUACAO_AGUARDANDO_PAGAMENTO);

/**
 * Pedidos SEM taxa: finaliza de fato o rascunho, gerando o Protocolo/
 * DocumentoProtocolo final. É o único endpoint que respeita departamentoDestino.
 *
 * A API de Anexo de GEDs pede, no campo "protocoloId", o ID da linha em
 * DocumentoProtocolo — não o Protocolo.ID que /finalizar devolve com esse mesmo
 * nome (mesma pegadinha do Protocolo Online). Por isso, depois de finalizar,
 * busca o status pra pegar requerimento.DocumentoProtocolo e já devolve isso
 * pronto como "documentoProtocoloId".
 */
export async function finalizarRequerimentoSemTaxa({ solicitacaoId, config, formData, user, oabData }) {
  const { tiposAssunto, tipoPedido, comissaoRequerida } = resolveProtocoloDados(config.protocoloConfig, formData, oabData);
  const departamentoDestino = config.protocoloConfig.departamentoDestino || DEPARTAMENTO_DESTINO_PADRAO;
  const { pessoaId, usuarioId } = protocoloService.extrairIdsUsuario(user);

  const response = await protocoloService.finalizarProtocolo(solicitacaoId, {
    pessoaId: Number(pessoaId),
    usuarioId: Number(usuarioId),
    tiposAssunto,
    // tipoPedido é sempre obrigatório em /finalizar — vem resolvido do
    // protocoloConfig do pedido (fixo ou condicional, ver protocoloResolverService).
    tipoPedido,
    // só é enviado de fato (não-undefined) pelo pedido de Comissões Advogado
    // (modeloRequerimento 260, o único com `comissaoRequeridaField` no
    // protocoloConfig) — nos demais (incl. Comissões Estagiário, 266) o
    // resolver devolve undefined e o JSON.stringify do axios simplesmente
    // omite a chave.
    comissaoRequerida,
    departamentoDestino,
    registroConselho: oabData?.registroConselho,
    tipoInscricaoRemetente: oabData?.tipoInscricaoRemetente
  });

  const result = response.data || response;

  const statusResponse = await protocoloService.buscarStatusProtocolo(solicitacaoId);
  const status = statusResponse.data || statusResponse;
  const documentoProtocoloId = status?.requerimento?.DocumentoProtocolo || null;

  return {
    numeroProtocolo: result?.numeroProtocolo,
    protocoloId: result?.protocoloId,
    documentoProtocoloId
  };
}
